import React from 'react';

import { useGetFace } from '@vlabs/api/artemy/client';
import type { OutputFace, Uuid } from '@vlabs/api/artemy/model';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { CopyButton, Tooltip } from '@vlabs/uikit';
import type { WithClassNameProp } from '@vlabs/uikit/types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './LargeFaceCard.module.sass';
import { buildTestid } from '../../../lib/utils';
import type { BaseFaceCardPoint3Props } from '../base-face-card/BaseFaceCard.point3';
import { BaseFaceCardPoint3 } from '../base-face-card/BaseFaceCard.point3';

export interface LargeFaceCardPoint3Props extends
  Omit<BaseFaceCardPoint3Props, 'src' | 'size'>,
  WithClassNameProp {
  actions?: React.JSX.Element | ((face: OutputFace) => JSX.Element);
}

const displayName = 'LargeFaceCardPoint3';
const testid = buildTestid(displayName);

export function LargeFaceCardPoint3({
  faceId,
  actions = undefined,
  'data-testid': dataTestId = undefined,
  className = undefined,
  ...props
}: LargeFaceCardPoint3Props): React.JSX.Element {
  const { t } = useTranslation();

  const { data: face } = useGetFace(
    faceId as Uuid,
    { targets: ['create_time', 'face_id', 'user_data', 'avatar'] },
    { query: { enabled: !!faceId } },
  );

  const userData = (face?.user_data?.length ?? 0) > 0 ? face?.user_data : '-';

  return (
    <div
      className={classNames(st.Root, className)}
      {...testid(faceId, dataTestId)}
    >
      <BaseFaceCardPoint3
        faceId={face?.face_id}
        size="large"
        src={face?.avatar ? `/api/${face.avatar}` : undefined}
        {...props}
      />
      <div
        className={st.FaceCardData}
        {...testid(faceId, 'Data', dataTestId)}
      >
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'CreateTime', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.created')}
          </span>
          <span
            className={st.DataValue}
            {...testid(faceId, 'Data', 'CreateTime', 'Value', dataTestId)}
          >
            {parseISOToLocale(face?.create_time)}
          </span>
        </div>
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'FaceId', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.face id')}
          </span>
          <span
            className={classNames(st.DataValue, st.CopyFaceId)}
            {...testid(faceId, 'Data', 'FaceId', 'Value', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              tooltipContent={face?.face_id ?? ''}
            >
              {face?.face_id ?? ''}
            </TruncatedText>
            <Tooltip
              position="bottom"
              tooltipContent={t('копировать')}
            >
              <CopyButton
                className={st.CopyButton}
                round
                value={face?.face_id}
              />
            </Tooltip>
          </span>
        </div>
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'UserData', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.information')}
          </span>
          <span
            className={st.DataValue}
            {...testid(faceId, 'Data', 'UserData', 'Value', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              tooltipContent={userData ?? ''}
            >
              {userData ?? ''}
            </TruncatedText>
          </span>
        </div>
      </div>
      {typeof actions === 'function' ? actions(face) : actions}
    </div>
  );
}
